// src/hooks/useSupabaseData.js — SOLSTICE v68
// Charge toutes les données depuis Supabase avec sélection EXPLICITE des colonnes
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { settingsApi } from '../services/api'

const SETTINGS_DEFAUT = {
  raisonSociale: 'SOLSTICE', adresse: '', telephone: '', email: '', siteWeb: '',
  nui: '', rccm: '', logo: '', agent: '',
  modeleDevis: 'classique', modeleFacture: 'classique', modeleRecu: 'classique',
  delaiPaiementDefaut: 30, tauxTVA: 19.25,
  penalitesRetardActives: false, tauxPenalitesJour: 0.03,
  messagePenalites: "En cas de retard de paiement, des pénalités de 0.03% par jour seront appliquées.",
  cgv: { activer: true, position: 'footer', texte: '' }
}

function mapDoc(row) {
  if (!row) return null
  return {
    id:                row.id,
    numero:            row.numero,
    type:              row.type,
    statut:            row.statut,
    date:              row.date,
    dateEcheance:      row.date_echeance,
    client:            row.client_snapshot || {},
    clientId:          row.client_id,
    objet:             row.objet,
    items:             row.items || [],
    sousTotal:         row.sous_total_ht || 0,
    tva:               row.montant_tva || 0,
    total:             row.total || 0,
    taxMode:           row.tax_mode || 'TTC',
    afficherModeTaxation: row.afficher_mode_taxation || false,
    remiseGlobale:     (() => {
      const totalFrais = (row.frais_supplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
      const totalHT = (row.total || 0) - (row.montant_tva || 0) - totalFrais;
      const montant = Math.max(0, Math.round(((row.sous_total_ht || 0) - totalHT) * 100) / 100);
      return { valeur: montant, type: montant > 0 ? 'montant' : 'aucune', montant };
    })(),
    conditions:        row.conditions,
    notes:             row.notes,
    devisOrigineId:    row.devis_origine_id,
    avoirSourceId:     row.avoir_source_id,
    methodePaiement:   row.methode_paiement,
    raisonAnnulation:  row.raison_annulation,
    paiements:         row.paiements || [],
    montantAvance:     Number(row.montant_avance)  || 0,
    montantPaye: (() => {
      const fromPaiements = (row.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
      if (fromPaiements > 0) return fromPaiements;
      if (Number(row.montant_paye) > 0) return Number(row.montant_paye);
      return Number(row.montant_avance) || 0;
    })(),
    montantRestant:    Number(row.montant_restant) || 0,
    fraisSupplementaires: row.frais_supplementaires || [],
    penalitesRetard:   row.penalites_retard || {},
    createdBy:         row.created_by,
    updatedAt:         row.updated_at,
    lockedBy:          row.locked_by,
    lockedAt:          row.locked_at,
  }
}

function mapArticle(row) {
  if (!row) return null
  return {
    id: row.id, ref: row.ref,
    codeArticle: row.code_article, designation: row.designation,
    description: row.description, domaine: row.domaine,
    categoreMere: row.categorie_mere, codeCategorieMere: row.code_categorie_mere,
    categorieFille: row.categorie_fille || row.code_categorie_fille,
    codeCategorieFille: row.code_categorie_fille,
    marque: row.marque, refFournisseur: row.ref_fournisseur,
    unite: row.unite || 'pièce', prixUnitaire: row.prix_unitaire || 0,
    tva: row.tva || 'TTC', actif: row.actif,
  }
}


// Mapper BC DB → app (snake_case → camelCase)
function mapBC(row) {
  if (!row) return null
  return {
    id:              row.id,
    numero:          row.numero,
    statut:          row.statut,
    date:            row.date,
    dateLivraison:   row.date_livraison,
    dateMode:        row.date_mode || 'auto',
    fournisseur:     row.fournisseur_snapshot || {},
    fournisseurId:   row.fournisseur_id,
    objet:           row.objet,
    items:           row.items || [],
    sousTotal:       row.sous_total_ht || 0,
    tva:             row.montant_tva || 0,
    total:           row.total || 0,
    taxMode:         row.tax_mode || 'TTC',
    conditions:      row.conditions,
    notes:           row.notes,
    raisonAnnulation: row.raison_annulation,
    createdBy:       row.created_by,
    updatedAt:       row.updated_at,
  }
}

// Mapper BC app → DB (camelCase → snake_case)
function bcToDb(bc) {
  return {
    statut:              bc.statut || 'en-attente',
    date:                bc.date,
    date_livraison:      bc.dateLivraison || null,
    date_mode:           bc.dateMode || 'auto',
    fournisseur_snapshot: bc.fournisseur || {},
    objet:               bc.objet || '',
    items:               bc.items || [],
    sous_total_ht:       bc.sousTotal || 0,
    montant_tva:         bc.tva || 0,
    total:               bc.total || 0,
    tax_mode:            bc.taxMode || 'TTC',
    conditions:          bc.conditions || '',
    notes:               bc.notes || '',
    raison_annulation:   bc.raisonAnnulation || null,
  }
}

// Sélection explicite de toutes les colonnes documents
const DOCS_SELECT = `
  id, numero, type, statut, date, date_echeance,
  client_id, client_snapshot, objet, items,
  sous_total_ht, montant_tva, total, tax_mode, remise_globale,
  conditions, notes, devis_origine_id, avoir_source_id,
  methode_paiement, raison_annulation,
  paiements, frais_supplementaires, penalites_retard,
  montant_avance, montant_paye, montant_restant,
  afficher_mode_taxation,
  locked_by, locked_at, created_by, updated_at
`

// Lit le JWT depuis localStorage sans passer par auth.getSession()
// Évite le blocage de refreshingDeferred lors d'une reconnexion Realtime simultanée
function getTokenFromStorage() {
  try {
    const raw = localStorage.getItem('solstice-auth')
    const s = raw ? JSON.parse(raw) : null
    return s?.access_token || s?.session?.access_token || ''
  } catch { return '' }
}

export function useSupabaseData() {
  const [documents,           setDocuments]           = useState([])
  const [clients,             setClients]             = useState([])
  const [fournisseurs,        setFournisseurs]         = useState([])
  const [bonsCommande,        setBonsCommande]         = useState([])
  const [bordereauxLivraison, setBordereauxLivraison] = useState([])
  const [catalogue,           setCatalogue]           = useState([])
  const [settings,            setSettings]            = useState(SETTINGS_DEFAUT)
  const [catalogueLoaded,     setCatalogueLoaded]     = useState(false)
  const [docsTotal,           setDocsTotal]           = useState(0)
  const [loading,             setLoading]             = useState(true)
  const [error,               setError]               = useState(null)
  const agentCodeCache        = useRef(null)
  // Utilisateur mis en cache via onAuthStateChange — aucun appel réseau au moment de sauvegarder
  const cachedUserRef         = useRef(null)

  useEffect(() => {
    // onAuthStateChange déclenche immédiatement INITIAL_SESSION sans appel réseau
    // — évite le conflit avec AuthContext qui appelle aussi getSession() au démarrage
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      cachedUserRef.current = session?.user ?? null
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    async function init() {
      setLoading(true)
      try {
        // PARALLÉLISER: charger settings + clients + fournisseurs + docs + BC en même temps
        const [settingsResult, clientsResult, fournisseursResult, docsResult, bcsResult, blsResult] = await Promise.all([
          settingsApi.get().catch(() => SETTINGS_DEFAUT),
          // Limiter à 500 clients
          supabase.from('clients').select('id, nom, email, telephone, adresse, ville, pays, nui, rccm, notes, actif').eq('actif', true).order('nom').limit(500),
          // Limiter à 500 fournisseurs
          supabase.from('fournisseurs').select('id, nom, contact, email, telephone, adresse, ville, pays, nui, rccm, notes, actif, compte_bancaire, delai_livraison').eq('actif', true).order('nom').limit(500),
          // Documents limités à 300 (chargement initial)
          supabase.from('documents').select(DOCS_SELECT, { count: 'exact' }).order('created_at', { ascending: false }).limit(300),
          // BC limités à 200
          supabase.from('bons_commande').select('id, numero, statut, date, fournisseur_snapshot, fournisseur_id, objet, items, sous_total_ht, montant_tva, total, tax_mode, conditions, notes, raison_annulation, date_livraison, date_mode, created_by, created_at, updated_at').order('created_at', { ascending: false }).limit(200),
          // BL limités à 100 (pas tous)
          supabase.from('bordereaux_livraison').select('id, numero, date, client_id, document_id, statut, created_at').order('created_at', { ascending: false }).limit(100)
        ])

        setSettings(settingsResult || SETTINGS_DEFAUT)
        const { data: cls } = clientsResult
        setClients(cls || [])
        const { data: frs } = fournisseursResult
        setFournisseurs(frs || [])
        const { data: docs, count } = docsResult
        setDocuments((docs || []).map(mapDoc))
        setDocsTotal(count || 0)
        const { data: bcs } = bcsResult
        setBonsCommande((bcs || []).map(mapBC))
        const { data: bls } = blsResult
        setBordereauxLivraison(bls || [])

        // Lazy-load catalogue APRÈS affichage initial (pas bloquant)
        setTimeout(() => chargerCatalogue(), 500)
      } catch (err) {
        console.error('Erreur init:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  const chargerCatalogue = useCallback(async () => {
    setCatalogueLoaded(false)
    try {
      let offset = 0
      const CHUNK_SIZE = 500  // Réduire taille pour plus de parallélisation
      const requests = []
      let hasMore = true

      // Charger les chunks de façon parallèle (3 requêtes à la fois)
      while (hasMore) {
        const nextRequests = []
        for (let i = 0; i < 3 && hasMore; i++) {
          const start = offset
          const end = offset + CHUNK_SIZE - 1
          nextRequests.push(
            supabase.from('catalogue')
              .select('id, ref, code_article, designation, description, domaine, categorie_mere, code_categorie_mere, categorie_fille, code_categorie_fille, marque, ref_fournisseur, unite, prix_unitaire, tva, actif')
              .eq('actif', true)
              .range(start, end)
              .order('domaine')
              .order('designation')
          )
          offset += CHUNK_SIZE
        }

        // Attendre les 3 requêtes parallèles
        const results = await Promise.all(nextRequests)

        for (const result of results) {
          const { data, error } = result
          if (error) throw error
          if (!data || data.length === 0) {
            hasMore = false
            break
          }
          const mapped = data.map(mapArticle)
          setCatalogue(prev => [...prev.filter(a => !mapped.find(m => m.ref === a.ref)), ...mapped])
          if (data.length < CHUNK_SIZE) hasMore = false
        }
      }
    } catch (err) { console.warn('Catalogue:', err.message) }
    finally { setCatalogueLoaded(true) }
  }, [])

  useEffect(() => {
    // Fetch complet après événement — p.new seul est incomplet sans REPLICA IDENTITY FULL
    const fetchDoc = async (id) => {
      const { data } = await supabase.from('documents').select(DOCS_SELECT).eq('id', id).single()
      return data ? mapDoc(data) : null
    }
    const ch = supabase.channel('docs-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'documents' },
        async p => {
          const doc = await fetchDoc(p.new.id)
          if (doc) setDocuments(prev => prev.find(d => d.id === doc.id) ? prev : [doc, ...prev])
        })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'documents' },
        async p => {
          const doc = await fetchDoc(p.new.id)
          if (doc) setDocuments(prev => prev.map(d => d.id === doc.id ? doc : d))
        })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'documents' },
        p => setDocuments(prev => prev.filter(d => d.id !== p.old.id)))
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') console.log('[Realtime] docs-rt connecté')
        if (status === 'CHANNEL_ERROR') console.warn('[Realtime] docs-rt erreur canal')
        if (status === 'CLOSED') console.warn('[Realtime] docs-rt canal fermé')
      })
    return () => supabase.removeChannel(ch)
  }, [])

  // Fallback Realtime — si le WebSocket était coupé pendant une modification,
  // resynchroniser les documents dès que l'utilisateur revient sur l'onglet.
  // DÉLAI de 15s : évite que le re-fetch massif (300 docs) concurrence une sauvegarde
  // en cours et sature les connexions HTTP → causing timeouts sur les writes.
  useEffect(() => {
    let lastHidden = 0
    let refetchTimer = null
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHidden = Date.now()
        if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = null }
      } else if (document.visibilityState === 'visible') {
        if (Date.now() - lastHidden > 5000) {
          // Délai 30s : laisse les sauvegardes en cours se terminer avant le re-fetch
          refetchTimer = setTimeout(() => {
            supabase.from('documents')
              .select(DOCS_SELECT, { count: 'exact' })
              .order('created_at', { ascending: false })
              .limit(300)
              .then(({ data }) => {
                if (data) setDocuments(data.map(mapDoc))
              })
            refetchTimer = null
          }, 15000)
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (refetchTimer) clearTimeout(refetchTimer)
    }
  }, [])

  useEffect(() => {
    const ch = supabase.channel('clients-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' },
        () => supabase.from('clients').select('*').eq('actif', true).order('nom')
          .then(({ data }) => { if (data) setClients(data) }))
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [])

  const getCurrentUser = async () => {
    // getSession() lit depuis localStorage (pas de réseau), getUser() fait un appel réseau
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user ?? null
  }

  const saveDocument = useCallback(async (doc) => {
    const user = cachedUserRef.current

    const isValidUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    const isNew = !doc.id || !isValidUUID(doc.id)
    const payload = {
      type: doc.type, statut: doc.statut, date: doc.date,
      date_echeance: doc.dateEcheance || null,
      client_snapshot: doc.client || {}, objet: doc.objet,
      items: doc.items || [], sous_total_ht: doc.sousTotal || 0,
      montant_tva: doc.tva || 0, total: doc.total || 0,
      tax_mode: doc.taxMode || 'TTC',
      afficher_mode_taxation: doc.afficherModeTaxation || false, remise_globale: doc.remiseGlobale?.montant ?? doc.remiseGlobale?.valeur ?? 0,
      conditions: doc.conditions, notes: doc.notes,
      methode_paiement: doc.methodePaiement || null,
      raison_annulation: doc.raisonAnnulation || null,
      paiements: doc.paiements || [],
      frais_supplementaires: doc.fraisSupplementaires || [],
      penalites_retard: doc.penalitesRetard || {},
      montant_avance: doc.montantAvance || 0,
      montant_paye: doc.montantPaye || 0,
      montant_restant: doc.montantRestant || 0,
      updated_by: user?.id,
    }
    if (isNew) {
      if (!agentCodeCache.current) {
        const { data: profilData } = await supabase.from('profils').select('nom').eq('id', user.id).single()
        agentCodeCache.current = profilData?.nom || 'XX'
      }
      const agentCode = agentCodeCache.current
      const { data: num } = await supabase.rpc('get_next_numero', { p_type: doc.type, p_agent: agentCode })
      payload.numero = num || doc.numero
      payload.created_by = user?.id
      const { data, error } = await supabase.from('documents').insert(payload).select(DOCS_SELECT).single()
      if (error) throw error
      const mapped = mapDoc(data)
      setDocuments(prev => [mapped, ...prev])
      return mapped
    } else {
      // UPDATE via fetch() natif — bypasse auth.getSession() du client Supabase JS
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
      const ANON_KEY    = import.meta.env.VITE_SUPABASE_ANON_KEY
      const resp = await fetch(`${SUPABASE_URL}/rest/v1/documents?id=eq.${doc.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getTokenFromStorage()}`,
          'apikey': ANON_KEY,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const mapped = mapDoc({ ...payload, id: doc.id, numero: doc.numero })
      setDocuments(prev => prev.map(d => d.id === doc.id ? mapped : d))
      return mapped
    }
  }, [])

  const deleteDocument = useCallback(async (id) => {
    await supabase.from('documents').delete().eq('id', id)
    setDocuments(prev => prev.filter(d => d.id !== id))
  }, [])

  const saveClient = useCallback(async (c) => {
    const user = cachedUserRef.current
    if (!c.id) {
      const { data, error } = await supabase.from('clients')
        .insert({ ...c, created_by: user?.id, updated_by: user?.id }).select().single()
      if (error) throw error
      setClients(prev => [...prev, data]); return data
    } else {
      const { data, error } = await supabase.from('clients')
        .update({ ...c, updated_by: user?.id }).eq('id', c.id).select().single()
      if (error) throw error
      setClients(prev => prev.map(x => x.id === data.id ? data : x)); return data
    }
  }, [])

  const deleteClient = useCallback(async (id) => {
    await supabase.from('clients').update({ actif: false }).eq('id', id)
    setClients(prev => prev.filter(c => c.id !== id))
  }, [])

  const saveFournisseur = useCallback(async (f) => {
    const user = cachedUserRef.current
    const { id, compteBancaire, delaiLivraison, ...rest } = f
    const payload = { ...rest, compte_bancaire: compteBancaire ?? null, delai_livraison: delaiLivraison ?? null }
    if (!id) {
      // INSERT : laisser Supabase générer l'UUID (ne pas inclure id)
      const { data, error } = await supabase.from('fournisseurs')
        .insert({ ...payload, created_by: user?.id, updated_by: user?.id }).select().single()
      if (error) throw error
      setFournisseurs(prev => [...prev, data]); return data
    } else {
      const { data, error } = await supabase.from('fournisseurs')
        .update({ ...payload, updated_by: user?.id }).eq('id', id).select().single()
      if (error) throw error
      setFournisseurs(prev => prev.map(x => x.id === data.id ? data : x)); return data
    }
  }, [])

  const deleteFournisseur = useCallback(async (id) => {
    await supabase.from('fournisseurs').update({ actif: false }).eq('id', id)
    setFournisseurs(prev => prev.filter(f => f.id !== id))
  }, [])

  const getBonCommande = useCallback(async (id) => {
    // fetch() natif — bypasse auth.getSession() du client Supabase JS qui peut bloquer
    // si refreshingDeferred est en attente (reconnexion Realtime simultanée)
    const token = getTokenFromStorage()
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const ANON_KEY    = import.meta.env.VITE_SUPABASE_ANON_KEY
    const cols = 'id,numero,statut,date,fournisseur_snapshot,fournisseur_id,objet,items,sous_total_ht,montant_tva,total,tax_mode,conditions,notes,raison_annulation,date_livraison,date_mode,created_by,created_at,updated_at'
    const url = `${SUPABASE_URL}/rest/v1/bons_commande?id=eq.${id}&select=${cols}`
    const resp = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}`, 'apikey': ANON_KEY, 'Accept': 'application/json' }
    })
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const rows = await resp.json()
    if (!rows?.length) throw new Error('BC non trouvé')
    return mapBC(rows[0])
  }, [])

  const saveBonCommande = useCallback(async (bc) => {
    const user = cachedUserRef.current
    const payload = bcToDb(bc)
    payload.updated_by = user?.id
    // UUID Supabase = chaîne 8-4-4-4-12 hex. IDs temporaires = Date.now() (nombre) → nouveau BC.
    const isValidUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id))
    const isNew = !bc.id || !isValidUUID(bc.id)

    // Lire le JWT directement depuis localStorage — bypasse supabase.auth.getSession()
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const ANON_KEY    = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (isNew) {
      if (!user) throw new Error('Utilisateur non authentifié — reconnectez-vous')
      if (!agentCodeCache.current) {
        const { data: profilBC } = await supabase.from('profils').select('nom').eq('id', user.id).single()
        agentCodeCache.current = profilBC?.nom || 'XX'
      }
      const agentBC = agentCodeCache.current
      const { data: num } = await supabase.rpc('get_next_numero', { p_type: 'bc', p_agent: agentBC })
      payload.numero = num
      payload.created_by = user?.id
      const { data, error } = await supabase.from('bons_commande').insert(payload).select().single()
      if (error) throw error
      const mapped = mapBC(data)
      setBonsCommande(prev => [mapped, ...prev]); return mapped
    } else {
      // UPDATE via fetch() natif — contourne auth.getSession() du client Supabase JS
      // qui peut bloquer si refreshingDeferred est en attente (Realtime reconn. simultanée)
      const token = getTokenFromStorage()
      const url = `${SUPABASE_URL}/rest/v1/bons_commande?id=eq.${bc.id}`
      const resp = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': ANON_KEY,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '')
        throw new Error(`HTTP ${resp.status}: ${errBody}`)
      }
      const mapped = mapBC({ ...payload, id: bc.id, numero: bc.numero })
      setBonsCommande(prev => prev.map(b => b.id === bc.id ? mapped : b)); return mapped
    }
  }, [])

  const deleteBonCommande = useCallback(async (id) => {
    await supabase.from('bons_commande').delete().eq('id', id)
    setBonsCommande(prev => prev.filter(b => b.id !== id))
  }, [])

  const saveCatalogueArticle = useCallback(async (article, editingRef = null) => {
    const user = cachedUserRef.current
    const payload = {
      ref: article.ref, code_article: article.codeArticle,
      designation: article.designation, description: article.description,
      domaine: article.domaine,
      categorie_mere: article.categoreMere, code_categorie_mere: article.codeCategorieMere,
      categorie_fille: article.categorieFille, code_categorie_fille: article.codeCategorieFille,
      marque: article.marque, ref_fournisseur: article.refFournisseur,
      unite: article.unite || 'pièce', prix_unitaire: article.prixUnitaire || 0,
      tva: article.tva || 'TTC', actif: article.actif !== false,
      updated_by: user?.id,
    }
    if (!editingRef) {
      payload.created_by = user?.id
      const { data, error } = await supabase.from('catalogue').insert(payload).select().single()
      if (error) throw error
      const mapped = mapArticle(data)
      setCatalogue(prev => [...prev, mapped]); return mapped
    } else {
      const { data, error } = await supabase.from('catalogue').update(payload).eq('ref', editingRef).select().single()
      if (error) throw error
      const mapped = mapArticle(data)
      setCatalogue(prev => prev.map(a => a.ref === editingRef ? mapped : a)); return mapped
    }
  }, [])

  const deleteCatalogueArticle = useCallback(async (ref) => {
    await supabase.from('catalogue').update({ actif: false }).eq('ref', ref)
    setCatalogue(prev => prev.filter(a => a.ref !== ref))
  }, [])

  const toggleActifArticle = useCallback(async (ref) => {
    const article = catalogue.find(a => a.ref === ref)
    if (!article) return
    const { data } = await supabase.from('catalogue')
      .update({ actif: !article.actif }).eq('ref', ref).select().single()
    if (data) setCatalogue(prev => prev.map(a => a.ref === ref ? mapArticle(data) : a))
  }, [catalogue])

  const saveSettings = useCallback(async (s) => {
    const saved = await settingsApi.update(s)
    setSettings(saved); return saved
  }, [])

  // ── Locking optimiste ──────────────────────────────────────────────────────
  const lockDocument = useCallback(async (docId) => {
    if (!docId) return
    const user = cachedUserRef.current
    if (!user) return
    await supabase.from('documents')
      .update({ locked_by: user.id, locked_at: new Date().toISOString() })
      .eq('id', docId)
  }, [])

  const unlockDocument = useCallback(async (docId) => {
    if (!docId) return
    const user = cachedUserRef.current
    if (!user) return
    await supabase.from('documents')
      .update({ locked_by: null, locked_at: null })
      .eq('id', docId)
      .eq('locked_by', user.id)
  }, [])

  const checkDocumentLock = useCallback(async (docId) => {
    if (!docId) return null
    const user = cachedUserRef.current
    if (!user) return null
    const { data } = await supabase
      .from('documents')
      .select('locked_by, locked_at')
      .eq('id', docId)
      .single()
    if (!data?.locked_by) return null
    if (data.locked_by === user.id) return null
    // Verrou considéré périmé après 30 minutes
    if (Date.now() - new Date(data.locked_at).getTime() > 30 * 60 * 1000) return null
    // Récupérer le nom de l'utilisateur qui a le verrou
    const { data: p } = await supabase
      .from('profils').select('nom, prenom').eq('id', data.locked_by).single()
    const nom = p ? [p.prenom, p.nom].filter(Boolean).join(' ') : 'un autre utilisateur'
    return { nom }
  }, [])

  return {
    documents, clients, fournisseurs, bonsCommande,
    bordereauxLivraison, setBordereauxLivraison,
    catalogue, catalogueLoaded, settings,
    loading, error, docsTotal,
    saveDocument, deleteDocument,
    saveClient, deleteClient,
    saveFournisseur, deleteFournisseur,
    getBonCommande, saveBonCommande, deleteBonCommande,
    saveCatalogueArticle, deleteCatalogueArticle, toggleActifArticle,
    saveSettings, reloadCatalogue: chargerCatalogue,
    lockDocument, unlockDocument, checkDocumentLock,
  }
}
